/**
 * A link to an artifact page with the game's hover tooltip (on mouse hover and keyboard focus,
 * never on touch), for every artifact type.
 *
 * The tooltip's data loads only once it opens, inside `LoadedTooltip`, so a page full of links
 * neither queries anything nor needs a query client until someone actually hovers.
 */
import type { AnchorHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import type { ArtifactType } from '@thottbot/shared';
import { useTooltip } from '../api/client';
import { GameTooltip, type AnchorRect, type TalentContext, type TooltipKnown } from './tooltip/GameTooltip';
import { useHoverTooltip } from './tooltip/useHoverTooltip';

function describedBy(own: string | undefined, tooltipId: string | null): string | undefined {
  return [own, tooltipId].filter(Boolean).join(' ') || undefined;
}

function LoadedTooltip({
  id,
  anchor,
  type,
  slug,
  known,
  contextLines,
  talent,
}: {
  id: string;
  anchor: AnchorRect;
  type: ArtifactType;
  slug: string;
  known: TooltipKnown;
  contextLines?: string[];
  talent?: TalentContext;
}) {
  const q = useTooltip(type, slug);
  const state = q.isError ? 'error' : q.data ? 'ready' : 'loading';
  return <GameTooltip id={id} anchor={anchor} known={known} state={state} data={q.data} contextLines={contextLines} talent={talent} />;
}

export interface ArtifactLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  type: ArtifactType;
  slug: string;
  name: string;
  children?: ReactNode;
  quality?: number;
  /** Notes about this use of the item, drawn under the tooltip body (a build's enchant...). */
  contextLines?: string[];
  talent?: TalentContext;
}

export function ArtifactLink({
  type,
  slug,
  name,
  children,
  quality,
  contextLines,
  talent,
  className,
  'aria-describedby': ownDescribedBy,
  ...rest
}: ArtifactLinkProps) {
  const { id, anchor, handlers } = useHoverTooltip();
  const cls = className ?? (quality !== undefined ? `quality-${quality}` : undefined);
  return (
    <>
      <Link
        to={`/${type}/${slug}`}
        className={cls}
        aria-describedby={describedBy(ownDescribedBy, anchor ? id : null)}
        {...rest}
        {...handlers}
      >
        {children ?? name}
      </Link>
      {anchor && (
        <LoadedTooltip id={id} anchor={anchor} type={type} slug={slug} known={{ name, quality }} contextLines={contextLines} talent={talent} />
      )}
    </>
  );
}

export interface TooltipAnchorProps extends HTMLAttributes<HTMLSpanElement> {
  known: TooltipKnown;
  /** The tooltip body, drawn as-is (nothing is fetched). */
  lines?: string[];
  contextLines?: string[];
  talent?: TalentContext;
  children?: ReactNode;
}

/**
 * The same tooltip on something with no page to fetch (an empty slot, an item or talent the
 * database does not know yet): a focusable span showing what the caller already has.
 */
export function TooltipAnchor({ known, lines, contextLines, talent, children, className, 'aria-describedby': ownDescribedBy, ...rest }: TooltipAnchorProps) {
  const { id, anchor, handlers } = useHoverTooltip();
  return (
    <>
      <span
        tabIndex={0}
        className={`tooltip-anchor${className ? ` ${className}` : ''}`}
        aria-describedby={describedBy(ownDescribedBy, anchor ? id : null)}
        {...rest}
        {...handlers}
      >
        {children}
      </span>
      {anchor && <GameTooltip id={id} anchor={anchor} known={known} state="ready" lines={lines} contextLines={contextLines} talent={talent} />}
    </>
  );
}
